/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { localDate, shiftDate } from "../../shared/dates";
import { AssignModal } from "./AssignModal";
import { AutoDraftModal } from "./AutoDraftModal";
import { PeopleHours } from "./PeopleHours";
import { PublishModal } from "./PublishModal";
import type { Shift } from "./types";
import { mondayOf, useRota } from "./useRota";
import { WeekGrid } from "./WeekGrid";

// The week's rota: fill it from the day templates, assign people with
// warnings, keep gaps visible, then publish.
// See docs/plans/workday/staff_planning.md (phase 1c).
export const RotaSchedulePage = defineComponent({
  name: "RotaSchedulePage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const route = useRoute();
    const router = useRouter();
    // The shown week lives in the URL (?week=YYYY-MM-DD), so it survives a reload and can be shared.
    const fromQuery = typeof route.query.week === "string" && /^\d{4}-\d{2}-\d{2}$/.test(route.query.week) ? route.query.week : null;
    const weekStart = ref(mondayOf(fromQuery ?? localDate()));
    watch(weekStart, (w) => router.replace({ query: { ...route.query, week: w } }));
    const rota = useRota(weekStart);
    const editing = ref<{ shift: Shift | null; date: string } | null>(null);
    const publishing = ref(false);
    const drafting = ref(false);
    const busy = ref(false);

    onMounted(async () => {
      try {
        await rota.load();
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
      }
    });

    const weekLabel = computed(() => {
      const a = new Date(`${rota.days.value[0]}T12:00:00`);
      const b = new Date(`${rota.days.value[6]}T12:00:00`);
      return `${a.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${b.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
    });

    const run = async (action: () => Promise<void>) => {
      busy.value = true;
      try {
        await action();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        busy.value = false;
      }
    };

    const fill = () =>
      run(async () => {
        const { added } = await trpc.rota.shifts.fillFromTemplates.mutate(rota.range.value);
        toast.showToast(
          added ? t("workday.rota.filled").replace("{count}", String(added)) : t("workday.rota.filled_none"),
          { type: added ? "success" : "info" },
        );
        await rota.loadShifts();
      });

    const approve = (notify: boolean) =>
      run(async () => {
        const { published, emailed } = await trpc.rota.shifts.publish.mutate({ ...rota.range.value, notify });
        toast.showToast(
          t("workday.rota.published").replace("{count}", String(published)) +
            (emailed ? ` · ${t("workday.rota.emailed").replace("{count}", String(emailed))}` : ""),
          { type: "success" },
        );
        publishing.value = false;
        await rota.loadShifts();
      });

    const undoAuto = () =>
      run(async () => {
        if (!confirm(t("workday.auto.undo_confirm"))) return;
        await trpc.rota.shifts.undoAuto.mutate(rota.range.value);
        await rota.loadShifts();
      });

    const discard = () =>
      run(async () => {
        if (!confirm(t("workday.rota.discard_confirm"))) return;
        await trpc.rota.shifts.discardDrafts.mutate(rota.range.value);
        publishing.value = false;
        await rota.loadShifts();
      });

    const noSetup = computed(() => !rota.loading.value && (rota.roles.value.length === 0 || rota.people.value.length === 0));

    return () => {
      const c = rota.coverage.value;
      return (
        <div class="space-y-4">
          <div class="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-base-300 bg-base-100 p-3">
            <div class="flex items-center gap-1">
              <button class="btn btn-ghost btn-sm btn-square" aria-label={t("workday.rota.prev_week")} onClick={() => (weekStart.value = shiftDate(weekStart.value, -7))}>
                <span class="material-symbols-outlined">chevron_left</span>
              </button>
              <div class="min-w-48 text-center font-semibold">{weekLabel.value}</div>
              <button class="btn btn-ghost btn-sm btn-square" aria-label={t("workday.rota.next_week")} onClick={() => (weekStart.value = shiftDate(weekStart.value, 7))}>
                <span class="material-symbols-outlined">chevron_right</span>
              </button>
              <button class="btn btn-ghost btn-sm" onClick={() => (weekStart.value = mondayOf(localDate()))}>
                {t("workday.rota.this_week")}
              </button>
            </div>
            <div class="text-sm text-base-content/70">
              {t("workday.rota.coverage").replace("{shifts}", String(c.total)).replace("{filled}", String(c.filled)).replace("{total}", String(c.total))}
              {c.flagged > 0 && (
                <span class="ml-2 inline-flex items-center gap-1 text-error">
                  <span class="material-symbols-outlined text-sm">warning</span>
                  {t("workday.rota.flagged").replace("{count}", String(c.flagged))}
                </span>
              )}
            </div>
            <div class="flex flex-wrap gap-2">
              {rota.inWeek.value.some((s) => s.source === "auto" && s.status === "draft") && (
                <button class="btn btn-ghost btn-sm" disabled={busy.value} onClick={undoAuto}>
                  <span class="material-symbols-outlined text-base">undo</span>
                  {t("workday.auto.undo")}
                </button>
              )}
              <button class="btn btn-ghost btn-sm border-base-300" disabled={busy.value || !rota.settings.value} onClick={() => (drafting.value = true)}>
                <span class="material-symbols-outlined text-base">auto_awesome</span>
                {t("workday.auto.button")}
              </button>
              <button class="btn btn-ghost btn-sm border-base-300" disabled={busy.value} onClick={fill}>
                <span class="material-symbols-outlined text-base">view_timeline</span>
                {t("workday.rota.fill")}
              </button>
              <button class="btn btn-primary btn-sm" disabled={busy.value || c.drafts === 0} onClick={() => (publishing.value = true)}>
                <span class="material-symbols-outlined text-base">publish</span>
                {c.drafts ? t("workday.rota.publish_n").replace("{count}", String(c.drafts)) : t("workday.rota.all_published")}
              </button>
            </div>
          </div>

          {noSetup.value && (
            <div class="alert alert-info text-sm">
              <span class="material-symbols-outlined">info</span>
              <span>{t("workday.rota.setup_first")}</span>
              <RouterLink to="/app/workday/team" class="btn btn-sm">
                {t("workday.nav.team")}
              </RouterLink>
            </div>
          )}

          {rota.loading.value ? (
            <div class="flex justify-center p-10">
              <Loading />
            </div>
          ) : (
            <div class="grid grid-cols-1 gap-4 2xl:grid-cols-[1fr_16rem]">
              <WeekGrid
                days={rota.days.value}
                shifts={rota.inWeek.value}
                warnings={rota.warnings.value}
                templates={rota.templates.value}
                onOpen={(shift: Shift) => (editing.value = { shift, date: shift.shift_date })}
                onAdd={(date: string) => (editing.value = { shift: null, date })}
              />
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-1 2xl:content-start">
                <PeopleHours people={rota.people.value} hours={rota.hours.value} maxWeek={rota.settings.value?.max_hours_week ?? 45} />
                <div class="rounded-2xl border border-base-300 bg-base-100 p-4 text-xs text-base-content/60">
                  <div class="mb-2 font-semibold text-base-content">{t("workday.rota.legend")}</div>
                  <div class="flex items-center gap-2"><span class="h-3 w-5 rounded border border-dashed border-base-content/40" />{t("workday.rota.legend_draft")}</div>
                  <div class="mt-1 flex items-center gap-2"><span class="h-3 w-5 rounded border border-base-300 bg-base-100" />{t("workday.rota.legend_published")}</div>
                  <div class="mt-1 flex items-center gap-2"><span class="h-3 w-5 rounded border border-warning/60 bg-warning/15" />{t("workday.rota.legend_open")}</div>
                  <div class="mt-1 flex items-center gap-2"><span class="material-symbols-outlined text-sm text-error">warning</span>{t("workday.rota.legend_warning")}</div>
                </div>
              </div>
            </div>
          )}

          {editing.value && rota.settings.value && (
            <AssignModal
              shift={editing.value.shift}
              date={editing.value.date}
              allShifts={rota.shifts.value}
              members={rota.members.value}
              roles={rota.roles.value}
              settings={rota.settings.value}
              absences={rota.absences.value}
              onClose={async (changed: boolean) => {
                editing.value = null;
                if (changed) await rota.loadShifts();
              }}
            />
          )}
          {drafting.value && rota.settings.value && (
            <AutoDraftModal
              weekStart={weekStart.value}
              members={rota.members.value}
              settings={rota.settings.value}
              onClose={async (changed: boolean) => {
                drafting.value = false;
                if (changed) await rota.loadShifts();
              }}
            />
          )}
          {publishing.value && (
            <PublishModal
              title={weekLabel.value}
              drafts={c.drafts}
              open={rota.inWeek.value.filter((s) => s.status === "draft" && s.staff_id == null).length}
              flagged={c.flagged}
              busy={busy.value}
              onDiscard={discard}
              onApprove={approve}
              onClose={() => (publishing.value = false)}
            />
          )}
        </div>
      );
    };
  },
});
